// src/theme.js
//
// Design tokens (modernization phase 3). The VALUES live as CSS variables in
// index.css (:root for light, :root[data-theme="dark"] for dark); here we
// expose them to inline styles as `var(--…)` strings. Because every color is a
// variable reference rather than a literal, a component that reads
// theme.tokens.surface / theme.colors.primary automatically re-colors when the
// <html> data-theme attribute flips — no re-render or per-page change needed.
//
// Non-color tokens (radius, type scale) stay literal — they don't change
// between themes. Only add a var() indirection where a value differs by theme.
const tokens = {
  radius: { sm: 6, md: 10, lg: 14, pill: 999 },
  text: {
    xs: "0.72rem",   // micro-labels (uppercase section tags)
    sm: "0.85rem",   // secondary text
    md: "0.95rem",   // body
    lg: "1.12rem",   // card titles
    xl: "1.35rem",   // section headings
    xxl: "1.65rem",  // page title
  },
  ink: "var(--ink)",              // headings / strong text
  textMuted: "var(--text-muted)", // secondary text
  faint: "var(--faint)",          // hints, micro-labels
  border: "var(--border)",        // hairline card/input borders
  surface: "var(--surface)",
  surfaceMuted: "var(--surface-muted)",
  focus: "var(--focus)",
  success: { bg: "var(--success-bg)", fg: "var(--success-fg)", border: "var(--success-border)" },
  warn: { bg: "var(--warn-bg)", fg: "var(--warn-fg)", border: "var(--warn-border)" },
  danger: { bg: "var(--danger-bg)", fg: "var(--danger-fg)", border: "var(--danger-border)" },
  info: { bg: "var(--info-bg)", fg: "var(--info-fg)", border: "var(--info-border)" },
  field: { bg: "var(--field-bg)", fg: "var(--field-fg)", border: "var(--field-border)" }, // form inputs
  disabledBg: "var(--surface-muted)",
  navBg: "var(--nav-bg)",   // top/bottom chrome bar — stays dark in both themes
  navFg: "var(--nav-fg)",
  shadowCard: "var(--shadow-card)",
  shadowRaised: "var(--shadow-raised)",
};

const theme = {
  colors: {
    primary: "var(--primary)",   // SEAPA deep blue (lifts to a lighter tint in dark)
    accent: "var(--accent)",     // lighter blue highlight
    gold: "var(--gold)",         // compass gold
    lightGray: "var(--light-gray)", // panel background
    slate: "var(--slate)",       // text and subheaders
    white: "var(--surface)",     // "white" surfaces — adapt to the dark base
    success: "var(--success-fg)", // some pages reference theme.colors.success (was undefined)
    danger: "var(--danger-fg)",
  },
  font: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
  shadows: {
    card: "var(--shadow-card)",
  },
  tokens,
};

// Theme selection is driven entirely by the data-theme attribute on <html>
// (set pre-paint in index.html, toggled via lib/themeMode). These helpers read
// and set that attribute + persist the choice, and are the only writers of it.
export const THEME_KEY = "seapa-theme";

export function getThemeMode() {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

export function setThemeMode(mode) {
  const next = mode === "dark" ? "dark" : "light";
  try { document.documentElement.setAttribute("data-theme", next); } catch { /* SSR/no-DOM */ }
  try { localStorage.setItem(THEME_KEY, next); } catch { /* storage blocked */ }
  return next;
}

export function toggleThemeMode() {
  return setThemeMode(getThemeMode() === "dark" ? "light" : "dark");
}

export default theme;
export { tokens };
